import React, { useEffect } from 'react';
import {
  Box,
  Paper,
  Alert,
  Typography,
} from '@mui/material';
import {
  Login as LoginIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Waves as WavesIcon,
} from '@mui/icons-material';
import { useLoginForm } from './hooks/useLoginForm';
import { useAuth } from '../../auth/hooks/useAuth';
import { AUTH_CONSTANTS } from '../../constants/auth';
import { type LoginPageProps } from '../../types/auth';
import { TextFieldComponent } from '../common/TextFieldComponent';
import { CheckboxComponent } from '../common/CheckboxComponent';
import { ButtonComponent } from '../common/ButtonComponent';
import { useNavigate } from 'react-router-dom';

export const LoginPage: React.FC<LoginPageProps> = ({
  onLoginSuccess,
}) => {
  const { login, isLoading, error, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const {
    formData,
    errors,
    isValid,
    isSubmitting,
    handleFieldChange,
    handleSubmit,
    clearErrors,
  } = useLoginForm({
    onSubmit: async (credentials) => {
      clearError();
      await login(credentials);
      onLoginSuccess?.();
    },
  });

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    await handleSubmit();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(145deg, #0a1628 0%, #0f2847 35%, #0d3b66 60%, #1a4a6e 100%)',
      }}
    >
      {/* Animated wave pattern background */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.06,
          backgroundImage: `
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 40px,
              rgba(255,255,255,0.1) 40px,
              rgba(255,255,255,0.1) 41px
            ),
            repeating-linear-gradient(
              90deg,
              transparent,
              transparent 40px,
              rgba(255,255,255,0.05) 40px,
              rgba(255,255,255,0.05) 41px
            )
          `,
        }}
      />

      {/* Diagonal accent stripe */}
      <Box
        sx={{
          position: 'absolute',
          top: -200,
          right: -200,
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)',
          animation: 'pulse-glow 4s ease-in-out infinite',
          '@keyframes pulse-glow': {
            '0%, 100%': { opacity: 0.5, transform: 'scale(1)' },
            '50%': { opacity: 1, transform: 'scale(1.05)' },
          },
        }}
      />

      {/* Bottom wave accent */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -100,
          left: -200,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 70%)',
        }}
      />

      {/* Left Panel - Branding */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          px: 8,
        }}
      >
        <Box
          sx={{
            animation: 'fade-in-up 0.8s ease-out',
            '@keyframes fade-in-up': {
              from: { opacity: 0, transform: 'translateY(30px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          {/* Logo icon */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 4,
            }}
          >
            <WavesIcon sx={{ fontSize: 48, color: '#3b82f6' }} />
            <Typography
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontSize: '3.5rem',
                fontWeight: 700,
                color: '#fff',
                letterSpacing: '-0.02em',
                lineHeight: 1,
              }}
            >
              CPP
            </Typography>
          </Box>

          <Typography
            sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '1.1rem',
              color: 'rgba(255,255,255,0.5)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              mb: 5,
            }}
          >
            CPP. Isabel Gomez de Viza
          </Typography>

          <Box sx={{ maxWidth: 400 }}>
            <Typography
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontSize: '1.6rem',
                color: 'rgba(255,255,255,0.85)',
                lineHeight: 1.4,
                mb: 3,
              }}
            >
              Sistema de Gestion Empresarial
            </Typography>
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '0.95rem',
                color: 'rgba(255,255,255,0.4)',
                lineHeight: 1.7,
              }}
            >
              Control integral de produccion, catalogos, proveedores y trazabilidad para la industria pesquera.
            </Typography>
          </Box>

          {/* Decorative line */}
          <Box
            sx={{
              width: 60,
              height: 3,
              background: 'linear-gradient(90deg, #3b82f6, #10b981)',
              borderRadius: 2,
              mt: 5,
            }}
          />
        </Box>
      </Box>

      {/* Right Panel - Login Form */}
      <Box
        sx={{
          width: { xs: '100%', md: 480 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, md: 6 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 5 },
            width: '100%',
            maxWidth: 420,
            borderRadius: 4,
            backgroundColor: 'rgba(255,255,255,0.97)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)',
            animation: 'slide-in 0.6s ease-out',
            '@keyframes slide-in': {
              from: { opacity: 0, transform: 'translateX(40px)' },
              to: { opacity: 1, transform: 'translateX(0)' },
            },
          }}
        >
          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
              <WavesIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
              <Typography
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: '#0f2847',
                }}
              >
                CPP
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '0.7rem',
                color: '#94a3b8',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
              }}
            >
              CPP. Isabel Gomez de Viza
            </Typography>
          </Box>

          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontSize: '1.75rem',
                fontWeight: 600,
                color: '#0f2847',
                mb: 0.5,
              }}
            >
              {AUTH_CONSTANTS.MESSAGES.welcome}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '0.9rem',
                color: '#64748b',
              }}
            >
              {AUTH_CONSTANTS.MESSAGES.loginSubtitle}
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              onClose={clearError}
              sx={{
                mb: 3,
                borderRadius: 2,
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleFormSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextFieldComponent
                label={AUTH_CONSTANTS.FIELD_LABELS.username || 'Username'}
                type="text"
                value={formData.username}
                onChange={(value) => handleFieldChange('username', value)}
                error={errors.username}
                disabled={isLoading || isSubmitting}
                autoComplete="username"
                startIcon={<PersonIcon color={errors.username ? 'error' : 'action'} />}
              />

              <TextFieldComponent
                label={AUTH_CONSTANTS.FIELD_LABELS.password}
                type="password"
                value={formData.password}
                onChange={(value) => handleFieldChange('password', value)}
                error={errors.password}
                disabled={isLoading || isSubmitting}
                autoComplete="current-password"
                startIcon={<LockIcon color={errors.password ? 'error' : 'action'} />}
                showPasswordToggle={true}
              />

              <CheckboxComponent
                label={AUTH_CONSTANTS.FIELD_LABELS.rememberMe}
                checked={formData.rememberMe}
                onChange={(checked) => handleFieldChange('rememberMe', checked)}
                disabled={isLoading || isSubmitting}
                color="primary"
              />
            </Box>

            <ButtonComponent
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={!isValid}
              loading={isLoading || isSubmitting}
              startIcon={<LoginIcon />}
              loadingText={AUTH_CONSTANTS.BUTTON_LABELS.loading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #0f2847 0%, #1a4a6e 100%)',
                boxShadow: '0 4px 15px rgba(15,40,71,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1a3a5c 0%, #2a5a7e 100%)',
                  boxShadow: '0 6px 20px rgba(15,40,71,0.4)',
                },
              }}
            >
              {AUTH_CONSTANTS.BUTTON_LABELS.login}
            </ButtonComponent>
          </Box>

          {/* Footer */}
          <Box sx={{ textAlign: 'center', mt: 4, pt: 3, borderTop: '1px solid #e2e8f0' }}>
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '0.75rem',
                color: '#94a3b8',
              }}
            >
              &copy; {new Date().getFullYear()} Nuvol Solutions
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
